import { arrayOrArray, TApiaFormButton, TApiaLoad } from '@apia/util';
import uniqueId from 'lodash-es/uniqueId';
import QueryString from 'qs';
import React from 'react';
import { Box } from '@apia/theme';
import {
  hasSucceedFormValidation,
  useFormContext,
  validationsStore,
} from '@apia/validations';
import { ISimpleButton, SimpleButton } from '@apia/components';
import { getFunction, TApiaApiMethodHandler } from '../ApiaApiHandler';
import ApiaApi from '../apiaApi';

type TApiaApiButtonsContainer = TApiaApiMethodHandler & {
  definition?: TApiaLoad | undefined;
};

type TApiaApiButton = Omit<TApiaFormButton, 'type'> &
  Pick<ISimpleButton, 'type'> & {
    buttonKey: string;
  };

const parseButtons = (buttons: TApiaFormButton[]): TApiaApiButton[] => {
  return buttons.map((currentButton) => ({
    ...currentButton,
    buttonKey: uniqueId(),
    type: ['submitAjax', 'submit'].includes(currentButton.type)
      ? 'submit'
      : 'button',
  }));
};

const NonMemoizedApiaApiButtonsContainer = (
  props: TApiaApiButtonsContainer,
) => {
  const buttonElements = React.useMemo(
    () => arrayOrArray(props.definition?.form.buttons?.button ?? []),
    [props.definition?.form.buttons?.button],
  );
  const buttons = React.useMemo(
    () => parseButtons(buttonElements),
    [buttonElements],
  );
  const modalConfiguration = React.useMemo(
    () => props.configuration?.modalConfiguration,
    [props.configuration?.modalConfiguration],
  );
  const methodsPath = React.useMemo(
    () => props.configuration?.methodsPath,
    [props.configuration?.methodsPath],
  );
  const { name: apiaApiForm } = useFormContext();

  const renderButton = React.useCallback(
    (currentButton: TApiaApiButton) => {
      const key = currentButton.buttonKey;
      const className = `handler__${currentButton.type ?? ''}`;
      const onClick = () => {
        void (async function submitForm() {
          const validationResult =
            await validationsStore.validateForm(apiaApiForm);
          if (!hasSucceedFormValidation(validationResult)) {
            return;
          }
          const { submitValues } = validationResult;

          function runButtonMethod() {
            void (async () => {
              if (currentButton.onclick) {
                const actions = currentButton.onclick.split(';');
                // eslint-disable-next-line no-restricted-syntax
                for await (const action of actions) {
                  const method = await getFunction(action, {
                    ...props,
                  });

                  if (method) {
                    method({
                      currentUrl: props.definition?.form.action ?? 'noUrl',
                    });
                  } else {
                    throw new Error(
                      `The requested action is not defined: "${
                        props.definition?.form.action ?? ''
                      }"`,
                    );
                  }
                }
              }
            })();
          }

          if (props?.definition && currentButton.type === 'submit') {
            const formData = new FormData();
            Object.entries(submitValues).forEach(([name, value]) => {
              formData.append(name, (value as string | Blob) ?? '');
            });

            const hasContext = props?.definition.form.action.match(
              new RegExp(`^${window.CONTEXT}/`),
            );
            const url = `${hasContext ? '' : window.CONTEXT}${
              !props?.definition.form.action.startsWith('/') ? '/' : ''
            }${props?.definition.form.action}`;

            props.setState((current) => ({
              ...current,
              isLoading: true,
            }));

            void ApiaApi.post(url, {
              postData: props.state.isMultipart
                ? formData
                : QueryString.stringify(
                    [...formData.entries(), ['isAjax', true]].reduce<
                      Record<string, string>
                    >((accumulated, [name, value]) => {
                      const retValue = { ...accumulated };
                      retValue[name.toString()] = value.toString();
                      return retValue;
                    }, {}),
                  ),
              handleLoad: true,
              notificationsCategory: 'apiaApiHandler',
              modalConfiguration,
              methodsPath,
            }).finally(() => {
              runButtonMethod();
              if (props?.definition?.form.closeOnSubmit) {
                props.close();
              }
            });
          } else {
            runButtonMethod();
          }
        })();
      };
      return (
        <SimpleButton
          className={className}
          disabled={props.state.disabled}
          id={currentButton.id || currentButton.text}
          isLoading={props.state.isLoading}
          key={key}
          title={currentButton.text}
          type={currentButton.type}
          onClick={onClick}
        >
          {currentButton.text}
        </SimpleButton>
      );
    },
    [apiaApiForm, methodsPath, modalConfiguration, props],
  );

  return (
    buttons.length > 0 && (
      <Box className="handler__form__buttons">
        {buttons.map((currentButton) => renderButton(currentButton))}
      </Box>
    )
  );
};

export const ApiaApiButtonsContainer = React.memo(
  NonMemoizedApiaApiButtonsContainer,
);
